import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './CenteredModal.css';

export default function CenteredModal({
  isOpen,
  onClose,
  title,
  subtitle,
  className,
  onDelete,
  onDeleteSeries,
  isRecurringEvent = false,
  children
}) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Reset delete confirmation when modal opens/closes
  useEffect(() => {
    if (!isOpen) setShowDeleteConfirm(false);
  }, [isOpen]);

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = originalOverflow; };
    }
  }, [isOpen]);

  // Single gatekeeper: if not open, render nothing.
  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !showDeleteConfirm) {
      onClose();
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (onDelete) {
      await onDelete();
      setShowDeleteConfirm(false);
    }
  };

  const handleConfirmDeleteSeries = async () => {
    if (onDeleteSeries) {
      await onDeleteSeries();
      setShowDeleteConfirm(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  // Portal to document.body so all modals render at top level —
  // prevents DOM nesting issues and ensures consistent z-index stacking.
  return createPortal(
    <div 
      className="centered-modal-backdrop centered-modal-backdrop-active"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div 
        className={`centered-modal-content centered-modal-content-active${className ? ` ${className}` : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="centered-modal-header">
          <div className="centered-modal-title-group">
            {title && <h2 id="modal-title" className="centered-modal-title">{title}</h2>}
            {subtitle && <p className="centered-modal-subtitle">{subtitle}</p>}
          </div>
          <div className="centered-modal-header-actions">
            {onDelete && (
              <button
                className="centered-modal-delete"
                onClick={handleDeleteClick}
                aria-label="Delete"
                title="Delete"
                type="button"
              >
                🗑️
              </button>
            )}
            <button
              className="centered-modal-close"
              onClick={onClose}
              aria-label="Close modal"
              type="button"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="centered-modal-body">
          {children}
        </div>

        {showDeleteConfirm && (
          <div className="delete-confirmation-overlay" onClick={handleCancelDelete}>
            <div className="delete-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
              <h3>Delete {isRecurringEvent ? 'Event (Recurring)' : 'Item'}?</h3>
              {isRecurringEvent ? (
                <>
                  <p>This is a recurring event. What would you like to delete?</p>
                  <div className="delete-confirmation-actions recurring-delete-options">
                    <button 
                      className="btn-cancel"
                      onClick={handleCancelDelete}
                      type="button"
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn-delete-single"
                      onClick={handleConfirmDelete}
                      type="button"
                    >
                      Delete This Event Only
                    </button>
                    <button 
                      className="btn-delete-series"
                      onClick={handleConfirmDeleteSeries}
                      type="button"
                    >
                      Delete Entire Series
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p>Are you sure you want to delete this item? This action cannot be undone.</p>
                  <div className="delete-confirmation-actions">
                    <button 
                      className="delete-cancel-btn"
                      onClick={handleCancelDelete}
                      type="button"
                    >
                      Cancel
                    </button>
                    <button 
                      className="delete-confirm-btn"
                      onClick={handleConfirmDelete}
                      type="button"
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
